import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Checkbox, Field, Input, Select } from '@/components/ui/Field'
import { DataList, DataRow } from '@/components/ui/Page'
import { formatAgo, formatDateLong } from '@/lib/dates'
import { formatPhone } from '@/lib/format'
import { JOB_KIND_LABEL, type JobKind } from '@/lib/jobs'
import { supabase, type Row } from '@/lib/supabase'
import { JobLabels } from '@/features/labels/Labels'
import { usePackages } from '@/features/quotes/queries'
import { VenuePicker } from '@/features/venues/VenuePicker'
import { useUpdateContact, useUpdateJob, type JobDetail } from './queries'

/** The types Jena works with (28 Oct 2026); older ones still show on the projects that have them. */
const KINDS: JobKind[] = ['wedding', 'event', 'lifestyle', 'brand']

type Contact = Row<'contacts'>

/**
 * The quick overview of a project (28 Oct 2026): the couple with their email
 * and phone, the type, the date, the venue and, for a wedding, the collection
 * booked. Read first; "Edit" opens the same facts as a form. It sits in the
 * right-hand column of the project page (28 Oct 2026), so it lays out in one
 * column on a laptop.
 *
 * The stage and its two booking ticks stay here, small, because they are what
 * moves a project from enquiry to booked.
 */
export function ProjectDetails({ job }: { job: JobDetail }) {
  const [editing, setEditing] = useState(false)
  const packages = usePackages(true)
  const update = useUpdateJob(job.id)
  const collection = packages.data?.find((item) => item.id === job.package_id) ?? null
  const people = [job.primary_contact, job.partner_contact].filter((contact): contact is Contact => Boolean(contact))

  if (editing) return <DetailsForm job={job} onDone={() => setEditing(false)} />

  return (
    <div className="flex flex-col gap-4">
      <div className="grid gap-x-10 gap-y-2 md:grid-cols-2 lg:grid-cols-1">
        <DataList>
          {people.map((person, index) => (
            <DataRow key={person.id} label={index === 0 ? 'Couple' : ''}>
              <span className="flex flex-col items-end gap-0.5">
                <span className="font-semibold">{[person.first_name, person.last_name].filter(Boolean).join(' ')}</span>
                {person.email ? (
                  <a href={`mailto:${person.email}`} className="break-all underline decoration-[var(--color-rule-strong)] underline-offset-4">
                    {person.email}
                  </a>
                ) : (
                  <span className="text-taupe">No email</span>
                )}
                {person.phone ? (
                  <a href={`tel:${person.phone}`} className="text-taupe">
                    {formatPhone(person.phone)}
                  </a>
                ) : null}
              </span>
            </DataRow>
          ))}
          {people.length === 0 ? <DataRow label="Couple">—</DataRow> : null}
        </DataList>

        <DataList>
          <DataRow label="Type">{JOB_KIND_LABEL[job.kind]}</DataRow>
          {job.kind === 'associate' && job.associate_for ? <DataRow label="Shooting for">{job.associate_for}</DataRow> : null}
          <DataRow label="Date">{formatDateLong(job.event_date)}</DataRow>
          <DataRow label="Venue">{job.venue_name ?? job.venue?.name ?? '—'}</DataRow>
          {job.kind === 'wedding' ? <DataRow label="Package">{collection?.name ?? <span className="text-taupe">Not chosen</span>}</DataRow> : null}
          <DataRow label="Stage">
            {job.stage?.name ?? '—'} <span className="text-[length:var(--text-2xs)] text-taupe">since {formatAgo(job.stage_changed_at)}</span>
          </DataRow>
        </DataList>
      </div>

      {!job.booked_at ? (
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 rounded-md bg-paper px-3 py-2.5">
          <Checkbox
            label="Contract signed"
            checked={Boolean(job.contract_signed_at)}
            disabled={update.isPending}
            onChange={(event) => update.mutate({ contract_signed_at: event.target.checked ? new Date().toISOString() : null })}
          />
          <Checkbox
            label="Deposit received"
            checked={job.deposit_status === 'paid' || job.deposit_status === 'waived'}
            disabled={update.isPending}
            onChange={(event) => update.mutate({ deposit_status: event.target.checked ? 'paid' : 'pending' })}
          />
          <p className="text-[length:var(--text-2xs)] text-taupe">Moves to Booked once both are ticked.</p>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <JobLabels jobId={job.id} />
        <Button size="sm" variant="secondary" onClick={() => setEditing(true)}>
          Edit details
        </Button>
      </div>
    </div>
  )
}

interface PersonForm {
  id: string
  first_name: string
  last_name: string
  email: string
  phone: string
}

const toPerson = (contact: Contact): PersonForm => ({
  id: contact.id,
  first_name: contact.first_name,
  last_name: contact.last_name ?? '',
  email: contact.email ?? '',
  phone: contact.phone ?? '',
})

const orNull = (value: string) => value.trim() || null

function DetailsForm({ job, onDone }: { job: JobDetail; onDone: () => void }) {
  const packages = usePackages(true)
  const update = useUpdateJob(job.id)
  const updateContact = useUpdateContact(job.id)
  const [saving, setSaving] = useState(false)
  const [people, setPeople] = useState<PersonForm[]>(
    [job.primary_contact, job.partner_contact].filter((contact): contact is Contact => Boolean(contact)).map(toPerson),
  )
  const [form, setForm] = useState({
    kind: job.kind,
    title: job.title ?? '',
    event_date: job.event_date,
    venue_name: job.venue_name ?? '',
    package_id: job.package_id ?? '',
  })
  const kinds = KINDS.includes(job.kind) ? KINDS : [...KINDS, job.kind]
  const setPerson = (index: number, key: keyof PersonForm, value: string) =>
    setPeople((current) => current.map((person, i) => (i === index ? { ...person, [key]: value } : person)))

  async function save(event: React.FormEvent) {
    event.preventDefault()
    if (!form.event_date) {
      toast.error('Not saved yet', { description: 'The date is needed.' })
      return
    }
    if (people.some((person) => !person.first_name.trim())) {
      toast.error('Not saved yet', { description: 'Each person needs a first name.' })
      return
    }
    setSaving(true)
    try {
      for (const person of people) {
        await updateContact.mutateAsync({
          id: person.id,
          patch: {
            first_name: person.first_name.trim(),
            last_name: orNull(person.last_name),
            email: orNull(person.email),
            phone: orNull(person.phone),
          },
        })
      }
      await update.mutateAsync({
        kind: form.kind,
        title: orNull(form.title),
        event_date: form.event_date,
        venue_name: orNull(form.venue_name),
        package_id: form.kind === 'wedding' ? form.package_id || null : job.package_id,
      })
      toast.success('Saved')
      onDone()
    } finally {
      setSaving(false)
    }
  }

  return (
    <form noValidate onSubmit={(event) => void save(event)} className="flex flex-col gap-6">
      {people.map((person, index) => (
        <fieldset key={person.id} className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-1">
          <Field label="First name" required>
            <Input value={person.first_name} onChange={(event) => setPerson(index, 'first_name', event.target.value)} />
          </Field>
          <Field label="Surname">
            <Input value={person.last_name} onChange={(event) => setPerson(index, 'last_name', event.target.value)} />
          </Field>
          <Field label="Email">
            <Input type="email" value={person.email} onChange={(event) => setPerson(index, 'email', event.target.value)} />
          </Field>
          <Field label="Phone">
            <Input type="tel" value={person.phone} onChange={(event) => setPerson(index, 'phone', event.target.value)} />
          </Field>
        </fieldset>
      ))}

      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-1">
        <Field label="Type">
          <Select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value as JobKind })}>
            {kinds.map((key) => (
              <option key={key} value={key}>
                {JOB_KIND_LABEL[key]}
              </option>
            ))}
          </Select>
        </Field>
        {form.kind !== 'wedding' ? (
          <Field label="Name" hint='e.g. "CORE Fruit year-end function"'>
            <Input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          </Field>
        ) : null}
        <Field label="Date" required>
          <Input type="date" value={form.event_date} onChange={(event) => setForm({ ...form, event_date: event.target.value })} />
        </Field>
        {form.kind === 'wedding' ? (
          <Field label="Package">
            <Select value={form.package_id} onChange={(event) => setForm({ ...form, package_id: event.target.value })}>
              <option value="">Not chosen</option>
              {(packages.data ?? []).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                  {item.active ? '' : ' (no longer offered)'}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </div>

      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-1">
        <Field label="Venue from your library" hint="Brings its distance with it, for quotes and travel.">
          <VenuePicker value={job.venue_id} onChange={(venueId) => void linkVenue(venueId)} />
        </Field>
        <Field label="Venue name" hint="As it shows on the documents and in their portal.">
          <Input value={form.venue_name} onChange={(event) => setForm({ ...form, venue_name: event.target.value })} />
        </Field>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" variant="primary" loading={saving} loadingLabel="Saving">
          Save
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )

  /**
   * Linking a venue copies its distance onto the job, and its name and address
   * if those are still blank. Copied rather than referenced so that editing the
   * venue later never silently rewrites a wedding that has already been quoted.
   */
  async function linkVenue(venueId: string | null) {
    if (venueId === null) {
      await update.mutateAsync({ venue_id: null })
      return
    }

    const { data } = await supabase.from('venues').select('*').eq('id', venueId).single()

    await update.mutateAsync({
      venue_id: venueId,
      venue_name: job.venue_name ?? data?.name ?? null,
      venue_address: job.venue_address ?? data?.formatted_address ?? null,
      venue_lat: job.venue_lat ?? data?.latitude ?? null,
      venue_lng: job.venue_lng ?? data?.longitude ?? null,
      ...(data?.distance_km !== null && data?.distance_km !== undefined
        ? {
            distance_km: data.distance_km,
            travel_duration_minutes: data.travel_duration_minutes,
            distance_source: 'venue' as const,
            distance_calculated_at: new Date().toISOString(),
          }
        : {}),
    })
    if (!form.venue_name && data?.name) setForm((current) => ({ ...current, venue_name: data.name }))
    toast.success('Venue linked')
  }
}
